import useClevertapForHomepage from '@/src/hooks/analytics/clevertap/homepage'
import { AI_ACTION } from '@/src/utils/analytics/ai/aiEvents'
import { isCleartrip } from '@/src/utils/generalUtils/isCleartrip'
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome'
import FlightRoundedIcon from '@mui/icons-material/FlightRounded'
import { Box, Chip, Stack, Typography } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { useTranslations } from 'next-intl'
import { useLayoutEffect, useRef, useState } from 'react'

export type AIModeToggleTheme = 'yellow-blue' | 'blue-orange'
export type AIModeToggleValue = 'search' | 'ai'

const themes: Record<
    AIModeToggleTheme,
    { primary: string; gradient: string; glow: string }
> = {
    'yellow-blue': {
        primary: '#184E92',
        gradient: 'linear-gradient(135deg, #184E92 0%, #2a6bbd 60%, #D8C969 140%)',
        glow: '0 10px 28px -10px rgba(24,78,146,0.55)'
    },
    'blue-orange': {
        primary: '#3366CC',
        gradient: 'linear-gradient(135deg, #3366CC 0%, #4a7de0 55%, #F36821 140%)',
        glow: '0 10px 28px -10px rgba(51,102,204,0.55)'
    }
}

interface AIModeToggleMuiProps {
    value: AIModeToggleValue
    onChange: (value: AIModeToggleValue) => void
    deviceType: 'mobile' | 'desktop'
}

export const AIModeToggleHome = ({
    value,
    onChange,
    deviceType
}: AIModeToggleMuiProps) => {
    const t = useTranslations('common')
    const { triggerHomeAIEvent } = useClevertapForHomepage()
    const muiTheme = useTheme()
    const isRTL = muiTheme.direction === 'rtl'
    const variant: AIModeToggleTheme = isCleartrip() ? 'blue-orange' : 'yellow-blue'
    const theme = themes[variant]
    const isAI = value === 'ai'
    const searchLabel = t('search')
    const aiModeLabel = isRTL ? 'ابحث بالذكاء الاصطناعي' : 'AI Search'
    const betaLabel = t('beta')
    const tabListRef = useRef<HTMLDivElement | null>(null)
    const searchTabRef = useRef<HTMLDivElement | null>(null)
    const aiTabRef = useRef<HTMLDivElement | null>(null)
    const [indicatorMetrics, setIndicatorMetrics] = useState({
        inlineStart: 5,
        width: isAI ? (deviceType === 'mobile' ? 132 : 140) : (deviceType === 'mobile' ? 100 : 110)
    })

    const handleChange = (nextValue: AIModeToggleValue) => {
        if (nextValue !== value) {
            const enteringAI = nextValue === 'ai'
            triggerHomeAIEvent({
                action_name: enteringAI ? AI_ACTION.ENTER_AI_MODE : AI_ACTION.EXIT_AI_MODE,
                action_type: 'mode_toggle',
                action_value: nextValue,
                aimode: enteringAI,
                ai_layout: deviceType
            })
        }
        onChange?.(nextValue)
    }

    useLayoutEffect(() => {
        const updateIndicator = () => {
            const tabList = tabListRef.current
            const selectedTab = isAI ? aiTabRef.current : searchTabRef.current

            if (!tabList || !selectedTab) return

            const tabListRect = tabList.getBoundingClientRect()
            const selectedTabRect = selectedTab.getBoundingClientRect()
            const inlineStart = isRTL
                ? tabListRect.right - selectedTabRect.right
                : selectedTabRect.left - tabListRect.left
            const nextMetrics = {
                inlineStart,
                width: selectedTabRect.width
            }

            setIndicatorMetrics(prev =>
                prev.inlineStart === nextMetrics.inlineStart && prev.width === nextMetrics.width
                    ? prev
                    : nextMetrics
            )
        }

        updateIndicator()

        const resizeObserver =
            typeof ResizeObserver !== 'undefined' ? new ResizeObserver(updateIndicator) : null

        if (resizeObserver) {
            if (tabListRef.current) resizeObserver.observe(tabListRef.current)
            if (searchTabRef.current) resizeObserver.observe(searchTabRef.current)
            if (aiTabRef.current) resizeObserver.observe(aiTabRef.current)
        }

        window.addEventListener('resize', updateIndicator)

        return () => {
            resizeObserver?.disconnect()
            window.removeEventListener('resize', updateIndicator)
        }
    }, [aiModeLabel, betaLabel, deviceType, isAI, isRTL, searchLabel])

    return (
        <Box
            ref={tabListRef}
            role="tablist"
            aria-label="Mode toggle"
            sx={{
                position: 'relative',
                display: 'inline-flex',
                p: '5px',
                borderRadius: 999,
                bgcolor: '#fff',
                border: '1px solid rgba(15,27,61,0.08)',
                boxShadow:
                    '0 1px 0 rgba(255,255,255,0.6) inset, 0 8px 24px -12px rgba(15,27,61,0.18)',
                userSelect: 'none'
            }}
        >
            <Box
                aria-hidden
                sx={{
                    position: 'absolute',
                    top: 5,
                    bottom: 5,
                    insetInlineStart: indicatorMetrics.inlineStart,
                    width: indicatorMetrics.width,
                    borderRadius: 999,
                    background: isAI ? theme.gradient : 'rgba(15,27,61,0.06)',
                    boxShadow: isAI ? theme.glow : 'none',
                    transition:
                        'inset-inline-start .45s cubic-bezier(.6,.2,.2,1), width .45s cubic-bezier(.6,.2,.2,1), background .35s ease, box-shadow .35s ease',
                    zIndex: 0
                }}
            />

            <Stack
                ref={searchTabRef}
                role="tab"
                aria-selected={!isAI}
                onClick={() => handleChange('search')}
                direction="row"
                spacing={0.85}
                sx={{
                    position: 'relative',
                    zIndex: 1,
                    alignItems: 'center',
                    px: 2.25,
                    py: 1,
                    borderRadius: 999,
                    cursor: 'pointer',
                    color: !isAI ? theme.primary : 'rgba(15,27,61,0.55)',
                    transition: 'color .3s ease',
                    minWidth: deviceType === 'mobile' ? 100 : 110,
                    justifyContent: 'center'
                }}
            >
                <FlightRoundedIcon
                    sx={{
                        fontSize: 18,
                        transform: 'rotate(-35deg)',
                        transition: 'transform .4s ease'
                    }}
                />
                <Typography sx={{ fontWeight: 700, fontSize: 14.5, letterSpacing: 0.2 }}>
                    {searchLabel}
                </Typography>
            </Stack>

            <Stack
                ref={aiTabRef}
                role="tab"
                aria-selected={isAI}
                onClick={() => handleChange('ai')}
                direction="row"
                spacing={0.85}
                sx={{
                    position: 'relative',
                    zIndex: 1,
                    alignItems: 'center',
                    px: 2.25,
                    py: 1,
                    borderRadius: 999,
                    cursor: 'pointer',
                    color: isAI ? '#fff' : 'rgba(15,27,61,0.55)',
                    transition: 'color .3s ease',
                    minWidth: deviceType === 'mobile' ? 132 : 140,
                    justifyContent: 'center'
                }}
            >
                <Box
                    sx={{
                        position: 'relative',
                        display: 'inline-flex',
                        flexShrink: 0,
                        '& .sparkle': {
                            animation: isAI ? 'aiSpin 2.4s linear infinite' : 'none'
                        },
                        '@keyframes aiSpin': {
                            '0%': { transform: 'rotate(0deg) scale(1)' },
                            '50%': { transform: 'rotate(180deg) scale(1.15)' },
                            '100%': { transform: 'rotate(360deg) scale(1)' }
                        }
                    }}
                >
                    <AutoAwesomeIcon className="sparkle" sx={{ fontSize: 18 }} />
                </Box>
                <Typography sx={{ fontWeight: 700, fontSize: 14.5, letterSpacing: 0.2 }}>
                    {aiModeLabel}
                </Typography>
                <Chip
                    label={betaLabel}
                    size="small"
                    sx={{
                        height: 18,
                        fontSize: 9.5,
                        fontWeight: 800,
                        letterSpacing: 0.7,
                        color: isAI ? '#fff' : theme.primary,
                        bgcolor: 'transparent',
                        border: `1.5px solid ${isAI ? 'rgba(255,255,255,0.85)' : 'rgba(15,27,61,0.25)'}`,
                        transition: 'all .3s ease',
                        '& .MuiChip-label': { px: 0.7 }
                    }}
                />
            </Stack>
        </Box>
    )
}
